<template>
  <div class="products-models-selector">
    <div v-if="loading" class="loader"></div>
    <div v-else>
      <div class="products-models-selector-cnt">
        <div
          class="navbar-background"
          :class="{
            'pre-order-multiple': orderType === OrderType.MultiplePreOrder,
          }">
          <nav v-if="productMatrixStore.productModels.size" class="jump-to-menu" data-cy="jump-to-menu">
            <div class="jump-to-main-groups">
              <button
                v-for="productMainGroupModel in mainGroupsProductModels"
                :key="productMainGroupModel.mainGroup"
                class="jump-to"
                :class="[{ selected: selectedMainGroup === productMainGroupModel.mainGroup }]"
                :data-cy="`jump-to-tab.${productMainGroupModel.mainGroup}`"
                :style="`background-image: url(${getJumpToImageUrl(productMainGroupModel.mainGroup)})`"
                v-on:click="scrollMeTo(productMainGroupModel.mainGroup)">
                {{
                  `${$t(`global.${productMainGroupModel.mainGroup}`)} ( ${stats.totalSelected[productMainGroupModel.mainGroup]} )`
                }}
              </button>
              <input
                v-model="searchText"
                id="search-products"
                data-cy="search-bar"
                class="search-input"
                type="text"
                :placeholder="t('matrix.product_group_search_placeholder')" />
            </div>
          </nav>
        </div>
        <div ref="productsModelsList" class="products-models-list" data-cy="products-model-list">
          <div v-if="!productMatrixStore.productModels.size">
            {{
              orderType === OrderType.PreOrder
                ? $t('matrix.no_assortment_pre_order')
                : $t('matrix.no_assortment_re_order')
            }}
          </div>
          <div v-else-if="noSearchResults" class="no-products-found-message" data-cy="no-products-found-message">
            {{ $t("matrix['no products were found while searching']") }}
          </div>
          <div v-else>
            <section
              v-for="productMainGroupModel in mainGroupsProductModels"
              :id="`${productMainGroupModel.mainGroup}-section`"
              :key="productMainGroupModel.mainGroup"
              :data-cy="`main-group-tab.${productMainGroupModel.mainGroup}`"
              class="main-group-section">
              <div class="section-actions">
                <div
                  :ref="(el) => setMainGroupRef(el as HTMLElement, productMainGroupModel.mainGroup)"
                  :data-main-group="productMainGroupModel.mainGroup"
                  class="section-title">
                  {{
                    `${$t(`global.${productMainGroupModel.mainGroup}`)} (${stats.totalSelected[productMainGroupModel.mainGroup]})`
                  }}
                </div>
                <div class="section-buttons">
                  <BackstageButton
                    :disabled="searchText !== '' || loading"
                    :variant="ButtonVariant.SECONDARY"
                    :data-cy="`select-all-button.${productMainGroupModel.mainGroup}-cnt`"
                    :data-cy-button="`select-all-button.${productMainGroupModel.mainGroup}`"
                    v-on:click="selectAllProductModels(productMainGroupModel.productModels)">
                    {{
                      $t('global.select_all_button_filter') +
                      ' ' +
                      $t(`global.${productMainGroupModel.mainGroup}`).toString().toLocaleLowerCase()
                    }}
                  </BackstageButton>
                  <BackstageButton
                    :disabled="searchText !== '' || loading"
                    :variant="ButtonVariant.SECONDARY"
                    :data-cy="`clear-all-button.${productMainGroupModel.mainGroup}-cnt`"
                    :data-cy-button="`clear-all-button.${productMainGroupModel.mainGroup}`"
                    v-on:click="clearAllProductModels(productMainGroupModel.productModels)">
                    {{
                      $t('global.clear_all_button_filter') +
                      ' ' +
                      $t(`global.${productMainGroupModel.mainGroup}`).toString().toLocaleLowerCase()
                    }}
                  </BackstageButton>
                </div>
              </div>
              <div class="product-model-cards">
                <ProductModelCardV2
                  v-for="productModel in productMainGroupModel.productModels"
                  :key="productModel.name"
                  :product-model="productModel"
                  :is-selected="selectedModels.has(productModel.name)"
                  :class="[{ selected: selectedModels.has(productModel.name) }]"
                  @click="updateProductModelSelection(productModel)"
                  @keyup.enter="updateProductModelSelection(productModel)"
                  @showProductModelDetailsPopup="showProductModelDetailsPopup(productModel)" />
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showProductModelDetails">
      <NotificationMessage :size="'full'" class="popup-new" v-on:close="closeProductModelDetailsPopup">
        <ViewProductModelDetailsV2
          :hide-select-button="hideSelectButton"
          :product-model="showProductModelDetails"
          :is-selected="selectedModels.has(showProductModelDetails.name)"
          v-on:toggle="toggleShowProductModelDetailsSelected"
          v-on:close="closeProductModelDetailsPopup" />
      </NotificationMessage>
    </div>
  </div>
</template>

<script setup lang="ts">
import { AllowableProductModelDto, CaseType, OrderType, ProductMainGroups } from '@onrunning/backstage-models';
import { useIntersectionObserver } from '@vueuse/core';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import BackstageButton from '@/components/common/BackstageButton.vue';
import NotificationMessage from '@/components/common/NotificationMessage.vue';
import ViewProductModelDetailsV2 from '@/components/products/ViewProductModelDetailsV2.vue';
import { useBPLogger } from '@/composables/business-process-logger';
import { useAllowableAssortment } from '@/composables/useAllowableAssortment';
import { ButtonVariant } from '@/models/button-variant.enum';
import { useProductMatrixStore } from '@/stores/product-matrix';
import ProductModelCardV2 from './ProductModelCardV2.vue';

interface Props {
  orderType: OrderType | CaseType;
  orderDate: string;
  orderNumber?: string;
  hideSelectButton?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  orderType: OrderType.ReOrder,
  hideSelectButton: false,
});

const { pageViewBPLog } = useBPLogger();
const { t } = useI18n();
const {
  fetchAllowableAssortments,
  loading,
  visibleProductModels,
  setSelectedMainGroup,
  selectedMainGroup,
  mainGroupsProductModels,
  searchText,
} = useAllowableAssortment();

const productMatrixStore = useProductMatrixStore();

const productsModelsList = ref<HTMLElement>();
const mainGroupRefs = ref<Record<string, HTMLElement>>({});
const showProductModelDetails = ref<AllowableProductModelDto | undefined>(undefined);
const page_visit_start = ref(0);

const selectedModels = computed(() => {
  const selected = new Set([...productMatrixStore.selectedProductModelsNames]);

  if (productMatrixStore.selectedItemNumbers.size > 0) {
    for (const [name, model] of productMatrixStore.productModels) {
      if (model.itemNumbers?.some((itemNumber) => productMatrixStore.selectedItemNumbers.has(itemNumber))) {
        selected.add(name);
      }
    }
  }

  return selected;
});

const noSearchResults = computed(() => {
  return !loading.value && !visibleProductModels.value.length;
});

const stats = computed(() => {
  const totalSelected: Record<ProductMainGroups, number> = {
    [ProductMainGroups.shoes]: 0,
    [ProductMainGroups.apparel]: 0,
    [ProductMainGroups.accessories]: 0,
  };

  selectedModels.value.forEach((name: string) => {
    const productModel = productMatrixStore.productModels.get(name);

    if (productModel?.mainGroup) {
      totalSelected[productModel.mainGroup] = (totalSelected[productModel.mainGroup] || 0) + 1;
    }
  });

  return { totalSelected };
});

const setupIntersectionObservers = () => {
  const observerOptions = {
    root: productsModelsList.value,
    threshold: 0.1,
    rootMargin: '-100px 0px -100px 0px',
  };

  Object.values(ProductMainGroups).forEach((mainGroup) => {
    const targetElement = computed(() => mainGroupRefs.value[mainGroup]);

    useIntersectionObserver(
      targetElement,
      ([{ isIntersecting }]) => {
        if (isIntersecting) {
          setSelectedMainGroup(mainGroup);
        }
      },
      observerOptions,
    );
  });
};

onMounted(async () => {
  page_visit_start.value = new Date().getTime();

  await fetchAllowableAssortments(props.orderType, props.orderDate, props.orderNumber);
});

onUnmounted(() => {
  const visitDuration = new Date().getTime() - page_visit_start.value;
  pageViewBPLog(true, { page_name: 'products-model-selector' }, visitDuration);
});

watch(
  loading,
  (newLoading) => {
    if (!newLoading) {
      setupIntersectionObservers();
    }
  },
  { immediate: true },
);

function setMainGroupRef(el: HTMLElement, mainGroup: string) {
  if (el) {
    mainGroupRefs.value[mainGroup] = el;
  }
}

function updateProductModelSelection(productModel: AllowableProductModelDto) {
  const isSelected = selectedModels.value.has(productModel.name);

  if (isSelected) {
    productMatrixStore.removeSelectedProduct(productModel);
  } else {
    productMatrixStore.addSelectedProduct(productModel);
  }
}

function selectAllProductModels(productModels: AllowableProductModelDto[]) {
  productMatrixStore.addAllSelectedProducts(productModels);
}

function clearAllProductModels(productModels: AllowableProductModelDto[]) {
  productMatrixStore.removeSelectedProducts(productModels);
}

function scrollMeTo(refName: string) {
  const element = mainGroupRefs.value[refName];

  if (!element) {
    return;
  }

  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  setSelectedMainGroup(refName as ProductMainGroups);
}

function showProductModelDetailsPopup(productModel: AllowableProductModelDto) {
  showProductModelDetails.value = productModel;
}

function closeProductModelDetailsPopup() {
  showProductModelDetails.value = undefined;
}

function toggleShowProductModelDetailsSelected() {
  if (!showProductModelDetails.value) return;

  updateProductModelSelection(showProductModelDetails.value);
  closeProductModelDetailsPopup();
}

function getJumpToImageUrl(mainGroup: ProductMainGroups): string {
  return new URL(`/src/assets/img/ico-${mainGroup}.png`, import.meta.url).href;
}
</script>

<style scoped lang="scss">
.products-models-selector {
  margin: 0 auto;

  * {
    scroll-margin-top: 180px;
  }

  @include breakpoint(medium) {
    * {
      scroll-margin-top: 140px;
    }
  }

  @include breakpoint(small) {
    * {
      scroll-margin-top: 270px;
    }
  }

  .products-models-selector-cnt {
    .navbar-background {
      @include navbar-background;
      top: 83px;

      @include breakpoint(medium) {
        top: 63px;
      }

      @include breakpoint(small) {
        top: 180px;
      }

      .no-products-found-message {
        padding: 0 0 20px 25px;
        text-align: left;
      }

      &.pre-order-multiple {
        top: 189px;

        @include breakpoint(medium) {
          top: 169px;
        }
      }
    }

    .jump-to-menu {
      @include common-container;
      @include container-background;
      height: fit-content;
      border-radius: 0;
      margin: 0 auto;

      .jump-to-main-groups {
        display: flex;
        align-items: center;
        justify-content: left;
        gap: 12px;
        flex-wrap: wrap;

        button.jump-to {
          border-radius: 21px;
          height: 42px;
          box-sizing: border-box;
          font-size: 14px;
          background-color: transparent;
          border: 1px solid var(--border-color);
          color: var(--primary-text-color);
          background-repeat: no-repeat;
          text-align: left;
          background-position: 4px;
          cursor: pointer;
          padding: 0 20px 0 40px;
          margin: 0;
          flex-shrink: 0;
          background-size: 32px;

          &.selected {
            outline: 2px solid var(--new-primary-color);
            background-color: var(--bg-remark-selected-color);
            padding-left: 39px;
            background-position-x: 3px;
          }
        }

        .search-input {
          border-radius: 21px;
          height: 42px;
          padding: 0 16px 0 40px;
          font-size: 14px;
          background: url('@/assets/img/ico-search.svg') 10px no-repeat;
          stroke: var(--primary-text-color);
          color: var(--primary-text-color);
          min-width: 100px;
          width: auto !important;
          max-width: 510px;
          flex: 1;
          @include ico-search('black');
        }
      }
    }

    .products-models-list {
      @include common-container;
      padding: 20px;

      .main-group-section {
        padding-top: 45px; // this will allow you to scroll 15px below your 30px header
        margin-top: -45px;

        .section-actions {
          display: flex;
          flex-wrap: wrap;
          padding: 16.5px 0;
          gap: 10px;
          align-items: center;

          .section-title {
            text-align: left;
            font-size: 1.25em;
            line-height: 1.5em;
            flex: 1;
            text-wrap: nowrap;
          }

          .section-buttons {
            display: flex;
            align-items: center;
            gap: 20px;

            button {
              margin-right: 20px;
              &:last-child {
                margin-right: 0px;
              }
            }
          }
        }
      }

      .product-model-cards {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
        justify-content: space-between;
        gap: 20px;

        @include breakpoint(medium) {
          grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
        }

        .product-model-card {
          width: 100%;
          aspect-ratio: 172.29 / 190;
          box-sizing: border-box;
          background-color: var(--primary-bg-color);
          color: var(--primary-text-color);
          border-radius: 4px;
          display: flex;
          flex-direction: column;
          justify-content: space-around;
          align-items: center;
          cursor: pointer;
          position: relative;

          &.selected {
            outline: 2px solid var(--new-primary-color);
          }

          @media (hover) {
            &:focus {
              border: 2px solid var(--bg-remark-selected-color);
            }

            &:hover {
              background-color: var(--bg-remark-selected-color);
            }

            &:hover,
            &:focus {
              .expand-image-btn {
                display: block;
              }
            }
          }

          .card-top {
            width: 100%;
            height: 63%;

            .img {
              height: 100%;
              width: 100%;
              background-size: contain;
              background-repeat: no-repeat;
              background-position: center center;
            }
          }

          .card-bottom {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 8px;
            width: 100%;
            height: 42px;

            span {
              text-align: left;
              font-size: 0.875em;
            }
          }

          .expand-image-btn {
            position: absolute;
            top: 9px;
            right: 12px;
            width: 24px;
            height: 24px;
            @include icon-image('@/assets/img/icons/expand.svg', var(--primary-text-color));
            @media (hover) {
              display: none;
            }
          }
        }
      }
    }
  }

  .product-groups-cnt {
    background-color: var(--primary-bg-color);
  }

  .popup-new:deep() {
    .popup {
      padding: 0;
      border-radius: 0;
      .popup-content {
        background-color: var(--bg-page-color);
      }
    }
  }
}

.dark .products-models-selector .products-models-selector-cnt .jump-to-menu .search-input {
  @include ico-search('white');
}

.dark
  .products-models-selector
  .products-models-selector-cnt
  .products-models-list
  .product-model-cards
  article.product-model-card {
  background-color: var(--bg-remark-selected-color);
}
</style>
